import { Link } from "react-router-dom";
import { ArrowRight, DoorOpen, Compass, Layers } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LOGO_URL } from "@/lib/api";
import useMemberSession from "@/hooks/useMemberSession";

const STEPS = [
    {
        n: "01",
        title: "Read the restlessness",
        body: "A short, unhurried conversation across three clarities — personal, business, external. No forms designed by committee.",
    },
    {
        n: "02",
        title: "Name the quiet lifeboat",
        body: "We listen for the craft, network and judgement you've quietly been carrying — and shape it into a direction of travel.",
    },
    {
        n: "03",
        title: "Take the smallest, kindest bet",
        body: "You leave with a concrete first-ten-conversations list, a mortal bet worth placing, and a 90-day plan. No pension roulette.",
    },
];

export default function Landing() {
    const { isMember, claims } = useMemberSession();
    return (
        <div className="min-h-screen flex flex-col bg-weave-bg">
            <Header />

            {/* HERO */}
            <section
                className="relative overflow-hidden"
                data-testid="hero-section"
            >
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
                    <img
                        src="https://images.unsplash.com/photo-1705493625048-f3ae8452d249?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8">
                        <p
                            className="editorial-label text-weave-teal mb-6"
                            data-testid="hero-eyebrow"
                        >
                            Opportunity Unlock · Experience, rewoven
                        </p>
                        <h1
                            className="text-5xl md:text-7xl font-light leading-[0.95] tracking-tightest text-weave-ink"
                            data-testid="hero-title"
                        >
                            Twenty years of craft.
                            <br />
                            <span className="relative inline-block">
                                <span className="relative z-10">
                                    One more chapter
                                </span>
                                <span
                                    aria-hidden
                                    className="absolute left-0 right-0 -bottom-1 h-3 bg-weave-yellow/70 -z-0"
                                />
                            </span>{" "}
                            worth writing.
                        </h1>
                        <p
                            className="mt-8 text-lg md:text-xl leading-relaxed text-weave-slate max-w-2xl"
                            data-testid="hero-description"
                        >
                            For senior professionals who feel the restlessness
                            but haven't yet named it. A grounded conversation
                            that reads your trajectory back to you, names the
                            quiet lifeboat you're already carrying, and hands
                            you the smallest, kindest bet worth placing next.
                            No hype. No hustle.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/diagnostic"
                                className="btn-primary text-base py-4 px-7"
                                data-testid="hero-start-cta"
                            >
                                <DoorOpen className="w-4 h-4" />
                                {isMember
                                    ? "Open the next door"
                                    : "Begin the conversation"}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            {!isMember && (
                                <a
                                    href="https://awake.wearetheweave.co.uk/login"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-ghost"
                                    data-testid="hero-club-login"
                                >
                                    Already in the 1% Club? Sign in →
                                </a>
                            )}
                            {isMember && (
                                <span
                                    className="text-sm text-weave-slate"
                                    data-testid="hero-member-greeting"
                                >
                                    You're in the room
                                    {claims?.e ? (
                                        <>
                                            ,{" "}
                                            <span className="text-weave-ink">
                                                {claims.e}
                                            </span>
                                        </>
                                    ) : null}
                                    .
                                </span>
                            )}
                        </div>
                        <p
                            className="mt-6 text-sm text-weave-muted"
                            data-testid="hero-founder-link"
                        >
                            Running a business whose engine has stalled?{" "}
                            <Link
                                to="/diagnostic?flow=business"
                                className="weave-link text-weave-ink"
                                data-testid="hero-founder-cta"
                            >
                                Start the founder diagnostic →
                            </Link>
                        </p>
                    </div>
                    <div className="md:col-span-4 md:pl-10 border-l border-weave-border md:border-l">
                        <p className="editorial-label text-weave-slate mb-3">
                            A line we live by
                        </p>
                        <blockquote className="text-2xl leading-snug text-weave-ink font-light">
                            "The people you've been are still with you.{" "}
                            <span className="text-weave-red">
                                This is about which one
                            </span>{" "}
                            gets to walk through next."
                        </blockquote>
                        <div className="mt-8 flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt=""
                                className="h-8 w-auto opacity-80"
                            />
                            <span className="text-xs text-weave-muted uppercase tracking-editorial">
                                Experience, rewoven
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* THREE STEPS */}
            <section
                className="border-y border-weave-ink bg-weave-surface-alt"
                data-testid="how-it-works-section"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-8 md:gap-10 mb-14">
                        <div className="md:col-span-4">
                            <p className="editorial-label text-weave-teal mb-3">
                                How it works
                            </p>
                            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-weave-ink leading-tight">
                                A ten-minute conversation, not a fifty-page deck.
                            </h2>
                        </div>
                        <div className="md:col-span-7 md:col-start-6 self-end">
                            <p className="text-lg text-weave-slate leading-relaxed">
                                We don't describe your career back to you.
                                We listen for the through-line you may not
                                have seen — and turn it into a door worth
                                walking through, sized against real UK data.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-px bg-weave-ink border border-weave-ink">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.n}
                                className="bg-weave-bg p-10 md:p-12"
                                data-testid={`step-card-${i}`}
                            >
                                <div className="flex items-baseline justify-between mb-8">
                                    <span className="text-5xl md:text-6xl font-light text-weave-ink">
                                        {s.n}
                                    </span>
                                    {i === 0 && (
                                        <Compass className="w-5 h-5 text-weave-teal" />
                                    )}
                                    {i === 1 && (
                                        <Layers className="w-5 h-5 text-weave-red" />
                                    )}
                                    {i === 2 && (
                                        <DoorOpen className="w-5 h-5 text-weave-yellow" />
                                    )}
                                </div>
                                <h3 className="text-xl font-medium text-weave-ink mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-weave-slate leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT YOU GET */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <p className="editorial-label text-weave-teal mb-3">
                            What you leave with
                        </p>
                        <h2 className="text-3xl md:text-5xl font-light leading-tight tracking-tight text-weave-ink">
                            A door worth walking through. Not the whole path — just the next kind step.
                        </h2>
                        <p className="mt-6 text-lg text-weave-slate leading-relaxed">
                            We don't hand you a five-year plan or a buffet of
                            options. We read your trajectory back to you,
                            name what you already carry, and give you the
                            smallest, kindest bet to place next.
                        </p>
                        <Link
                            to="/diagnostic"
                            className="btn-primary mt-8"
                            data-testid="mid-start-cta"
                        >
                            <DoorOpen className="w-4 h-4" />
                            Begin the conversation
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="md:col-span-7 md:col-start-6">
                        <ul className="divide-y divide-weave-border border-y border-weave-border">
                            {[
                                [
                                    "Personal Clarity",
                                    "Your trajectory read back to you. The values you refuse to compromise. The rhythm of work that would feel like coming home.",
                                ],
                                [
                                    "Business Clarity",
                                    "The quiet lifeboat you're already carrying. The shape of the model. The smallest mortal bet worth running in the next two weeks.",
                                ],
                                [
                                    "External Clarity",
                                    "One person, warmly described. The two or three phrases that make them say 'yes, that's me'. Where to find the first ten.",
                                ],
                                [
                                    "The shape of the room",
                                    "Sized in people and GBP against real UK sources — ONS, DWP, Gov.uk, YouGov. Not vanity metrics — the honest year-one figure.",
                                ],
                                [
                                    "A concrete first 90 days",
                                    "Kind, small, printable. What to do this week, this month, this quarter — without betting your pension.",
                                ],
                                [
                                    "Optional founder track",
                                    "Already run a business whose engine has stalled? The same door, sized differently — TAM/SAM/SOM, MVP, and a Five Whys diagnostic.",
                                ],
                            ].map(([title, body], idx) => (
                                <li
                                    key={title}
                                    className="py-5 flex gap-6 items-start"
                                    data-testid={`outcome-row-${idx}`}
                                >
                                    <span className="text-xs editorial-label text-weave-muted w-10 pt-1">
                                        0{idx + 1}
                                    </span>
                                    <div>
                                        <div className="text-weave-ink font-medium">
                                            {title}
                                        </div>
                                        <div className="text-weave-slate mt-1">
                                            {body}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="border-t border-weave-ink bg-weave-ink text-weave-bg">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8">
                        <p className="editorial-label text-weave-yellow mb-4">
                            You don't have to walk this alone.
                        </p>
                        <h2 className="text-3xl md:text-5xl font-light leading-tight tracking-tight">
                            Ready to open the next door?
                        </h2>
                        <p className="mt-5 text-lg text-weave-bg/70 max-w-2xl">
                            Ten unhurried minutes. Three clarities. One kind
                            step. Experience, rewoven — welcome to your
                            second act.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/diagnostic"
                            className="inline-flex items-center gap-2 bg-weave-bg text-weave-ink px-7 py-4 text-base font-medium hover:bg-weave-yellow transition-colors"
                            data-testid="footer-start-cta"
                        >
                            <DoorOpen className="w-4 h-4" />
                            Begin the conversation
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
